import { type ChakraProps } from '@chakra-ui/react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import React from 'react';

import type { NextPageWithLayout } from 'nextjs/types';

import config from 'configs/app';
import useQueryClientConfig from 'lib/api/useQueryClientConfig';
import { AppContextProvider } from 'lib/contexts/app';
import { ChakraProvider } from 'lib/contexts/chakra';
import { MarketplaceContextProvider } from 'lib/contexts/marketplace';
import { RewardsContextProvider } from 'lib/contexts/rewards';
import { ScrollDirectionProvider } from 'lib/contexts/scrollDirection';
import { SettingsContextProvider } from 'lib/contexts/settings';
import { growthBook } from 'lib/growthbook/init';
import useLoadFeatures from 'lib/growthbook/useLoadFeatures';
import useNotifyOnNavigation from 'lib/hooks/useNotifyOnNavigation';
import { I18nProvider } from 'lib/i18n/context';
import { getLocaleFromCookie, loadMessages, type Messages } from 'lib/i18n/core';
import { clientConfig as rollbarConfig, Provider as RollbarProvider } from 'lib/rollbar';
import { SocketProvider } from 'lib/socket/context';
import RewardsLoginModal from 'ui/rewards/login/RewardsLoginModal';
import AppErrorBoundary from 'ui/shared/AppError/AppErrorBoundary';
import AppErrorGlobalContainer from 'ui/shared/AppError/AppErrorGlobalContainer';
import GoogleAnalytics from 'ui/shared/GoogleAnalytics';
import Layout from 'ui/shared/layout/Layout';
import Web3ModalProvider, { Web3WalletUIProvider } from 'ui/shared/Web3ModalProvider';

import '@rainbow-me/rainbowkit/styles.css';
import 'lib/setLocale';
// import 'focus-visible/dist/focus-visible';

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  pageProps: AppProps['pageProps'] & {
    locale?: ReturnType<typeof getLocaleFromCookie>;
    messages?: Messages;
  };
};

const ERROR_SCREEN_STYLES: ChakraProps = {
  h: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  width: 'fit-content',
  maxW: '800px',
  margin: { base: '0 auto', lg: '0 auto' },
  p: { base: 4, lg: 0 },
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {

  useLoadFeatures();
  useNotifyOnNavigation();

  const queryClient = useQueryClientConfig();

  const getLayout = Component.getLayout ?? ((page) => <Layout>{ page }</Layout>);

  const resolvedPageProps = React.useMemo(() => ({
    cookies: pageProps.cookies || (typeof document !== 'undefined' ? document.cookie : ''),
    referrer: pageProps.referrer || (typeof document !== 'undefined' ? document.referrer : ''),
    query: pageProps.query || {},
    adBannerProvider: pageProps.adBannerProvider ?? null,
    apiData: pageProps.apiData ?? null,
  }), [ pageProps ]);

  return (
    <ChakraProvider cookies={ resolvedPageProps.cookies }>
      <RollbarProvider config={ rollbarConfig }>
        <AppErrorBoundary
          { ...ERROR_SCREEN_STYLES }
          Container={ AppErrorGlobalContainer }
        >
          <Web3ModalProvider>
            <AppContextProvider pageProps={ resolvedPageProps }>
              <QueryClientProvider client={ queryClient }>
                <I18nProvider initialLocale={ pageProps.locale } initialMessages={ pageProps.messages }>
                  <Web3WalletUIProvider>
                    <GrowthBookProvider growthbook={ growthBook }>
                      <ScrollDirectionProvider>
                        <SocketProvider url={ `${ config.api.socket }${ config.api.basePath }/socket/v2` }>
                          <RewardsContextProvider>
                            <MarketplaceContextProvider>
                              <SettingsContextProvider>
                                { getLayout(<Component { ...pageProps }/>) }
                                { config.features.rewards.isEnabled && <RewardsLoginModal/> }
                              </SettingsContextProvider>
                            </MarketplaceContextProvider>
                          </RewardsContextProvider>
                        </SocketProvider>
                      </ScrollDirectionProvider>
                    </GrowthBookProvider>
                  </Web3WalletUIProvider>
                </I18nProvider>
                <ReactQueryDevtools buttonPosition="bottom-left" position="left"/>
                <GoogleAnalytics/>
              </QueryClientProvider>
            </AppContextProvider>
          </Web3ModalProvider>
        </AppErrorBoundary>
      </RollbarProvider>
    </ChakraProvider>
  );
}

// 确保所有页面（包括没有 getServerSideProps 的页面）在 SSR 时都能获取到 cookie，
// 以便 ChakraProvider 的 cookieStorageManagerSSR 能正确读取颜色模式，避免亮/暗模式闪烁。
MyApp.getInitialProps = async(appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const cookies = appContext.ctx.req?.headers.cookie || (typeof document !== 'undefined' ? document.cookie : '');

  // 仅当页面级 getServerSideProps 未提供 cookies 时才注入
  if (!appProps.pageProps.cookies) {
    appProps.pageProps.cookies = cookies;
  }
  if (!appProps.pageProps.referrer) {
    appProps.pageProps.referrer = appContext.ctx.req?.headers.referer || '';
  }
  if (!appProps.pageProps.locale) {
    appProps.pageProps.locale = getLocaleFromCookie(cookies);
  }
  if (!appProps.pageProps.messages) {
    appProps.pageProps.messages = await loadMessages(appProps.pageProps.locale);
  }

  return appProps;
};

export default MyApp;
